import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Container, Pagination, } from 'react-bootstrap';
import { format } from 'date-fns';
import { FileText, Trash2, Plus, Eye, AlertCircle, RefreshCw, Pencil } from 'lucide-react';
import { getAssignments, deleteAssignment, storeAssignment, updateAssignment } from '../../Services/AssignmentService';
import { openFile } from '../../utils/helper';
import usePagination from '../../hooks/usePagination';
import ModalForm from "../../Components/ModalForm";
import Loader from '../../Components/Loader';
import Swal from 'sweetalert2';
import { Helmet } from "react-helmet-async";

const ManageAssignments = () => {
    const [showModal, setShowModal] = useState(false);
    const [id, setId] = useState('');
    const [validationerror, setValidationError] = useState('');
    const {
        currentPage,
        totalPages,
        data: assignments,
        loading,
        error,
        handlePageChange,
        fetchPageData,
    } = usePagination(getAssignments);

    useEffect(() => {
        if (assignments.length === 0) {
            fetchPageData();
        }
    }, [assignments]);

    const handleRefresh = () => {
        fetchPageData();
    };

    const handleDelete = async (assignmentId) => {
        await deleteAssignment(assignmentId);
        fetchPageData();
    };

    const validateFormData = (formData) => {
        const currentDate = format(new Date(), 'yyyy-MM-dd');


        const validatePdf = () => {
            const MAX_FILE_SIZE = 5 * 1024 * 1024;
            const { file } = formData;
            const validPdfType = 'application/pdf';

            if (!file && id) {
                return true;
            }

            if (!file) {
                setValidationError('Please upload a file,');
                return false;
            }

            if (formData.file && formData.file.size > MAX_FILE_SIZE) {
                setValidationError('File size exceeds limit (5MB),');
                return false;
            }

            if (file.type !== validPdfType) {
                setValidationError('Invalid file type. Please select a PDF file,');
                return false;
            }
            return true;
        }

        if (!id) {
            if (formData?.submit_by < currentDate) {
                setValidationError('The submission date must not be earlier than the current date,');
                return false;
            }
            return validatePdf();

        }

        if (!id) {
            return validatePdf();
        }

        if (id) {
            if (id && Object.values(formData).every(value => !value)) {
                setValidationError('You must fill out at least one field to update.');
                return false;
            }

            if (formData?.submit_by && formData?.submit_by < currentDate) {
                setValidationError('The submission date must not be earlier than the current date,');
                return false;
            }

            if (formData?.file) {
                return validatePdf();
            }
            return true;
        }
        return true;
    };

    const handleOpenModal = () => setShowModal(true);
    const handleCloseModal = () => {
        setShowModal(false);
        setId('');
        setValidationError('');
    }

    const handleFormSubmit = async (formData) => {
        if (validateFormData(formData)) {
            const formDataToSubmit = new FormData();
            if (formData?.title) formDataToSubmit.append('title', formData?.title);
            if (formData?.class) formDataToSubmit.append('class', formData?.class);
            if (formData?.subject) formDataToSubmit.append('subject', formData?.subject);
            if (formData?.description) formDataToSubmit.append('description', formData?.description);
            if (formData?.file) formDataToSubmit.append('file', formData?.file);
            if (formData?.submit_by) formDataToSubmit.append('submit_by', formData?.submit_by);

            !id
                ? await storeAssignment(formDataToSubmit)
                : await updateAssignment(id, formDataToSubmit);

            fetchPageData();
        }
        else {
            Swal.fire({ title: `${validationerror} Please try again.`, icon: "error" });
        }
    }

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center p-5">
                <Loader />
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="p-4 text-danger">
                <AlertCircle className="me-2" />
                {error}
            </Container>
        );
    }

    return (
        <>
          <Helmet>
                <title>Manage Assignments</title>
                <meta name="description" content="Oasis Educational Institute" />
            </Helmet>
            <Container className="py-5">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2 className="mb-0 fw-5">
                        <FileText className="me-2" />
                        <strong>Manage Assignments</strong>
                    </h2>
                    <div>
                        <Button
                            variant="outline-primary"
                            className="me-2"
                            onClick={handleRefresh}
                            disabled={loading}
                        >
                            <RefreshCw className="me-1" size={12} />
                            {loading ? 'Refreshing...' : 'Refresh'}
                        </Button>
                        <Button variant="primary" onClick={handleOpenModal} >
                            <Plus className="me-1" size={12} />
                            Create New
                        </Button>
                    </div>
                </div>
                {assignments.length === 0 ? (
                    <div className=" d-flex justify-content-center align-content-center text-center p-4 bg-light rounded">
                        <FileText size={48} className="text-muted mb-3" />
                        <p className="font-bold mb-2">No assignments found</p>
                    </div>
                ) : (
                    <Table hover responsive className="align-middle">
                        <thead className="bg-light">
                            <tr>
                                <th>#</th>
                                <th>Title</th>
                                <th>File Name</th>
                                <th>Class</th>
                                <th>Subject</th>
                                <th>Description</th>
                                <th>Assigned on</th>
                                <th>Submit By</th>
                                <th>Updated at</th>

                                <th className="text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {assignments.map((assignment, index) => (
                                <tr key={assignment.id}>
                                    <td>{index + 1}</td>
                                    <td> <Link onClick={() => openFile(assignment.file_id)}> {assignment.title} </Link></td>
                                    <td>{assignment.file_name}</td>
                                    <td>{assignment.class}</td>
                                    <td>{assignment.subject}</td>
                                    <td>{assignment.description}</td>
                                    <td>
                                        {format(new Date(assignment.created_at), 'MMM dd, yyyy')}
                                    </td>
                                    <td>{format(new Date(assignment.submit_by), 'MMM dd, yyyy')}

                                    </td>
                                    <td>
                                        {format(new Date(assignment.updated_at), 'MMM dd, yyyy')}
                                    </td>
                                    <td>
                                        <div className="d-flex justify-content-center gap-2">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => openFile(assignment.file_id)}
                                            >
                                                <Eye size={16} />
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                className="text-gray-600"
                                                size="sm"
                                                onClick={() => {
                                                    setId(assignment.id)
                                                    handleOpenModal()
                                                }}
                                            >
                                                <Pencil size={16} />
                                            </Button>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleDelete(assignment.id)}
                                            >
                                                <Trash2 size={16} />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
                {assignments.length !== 0 &&
                    (<Pagination>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                            <Pagination.Item
                                key={pageNumber}
                                active={pageNumber === currentPage}
                                onClick={() => handlePageChange(pageNumber)}
                            >
                                {pageNumber}
                            </Pagination.Item>
                        ))}
                    </Pagination>
                    )}

            </Container>
            <ModalForm
                show={showModal}
                onClose={handleCloseModal}
                module="assignments"
                id= {id?? id}
                title={!id ? 'Add new Assignment' : 'Edit Assignment'}
                description="Please ensure that the file is in PDF format and does not exceed a maximum size of 5MB."
                fields={[
                    { label: "Title", name: "title", type: "text", required: id ? false : true, placeholder: "Title (e.g., Topic, Chapter)" },
                    {
                        label: "Class", name: "class", type: "select", required: id ? false : true, options: [
                            { value: "", label: "Select Class" },
                            { value: "LKG", label: "LKG" },
                            { value: "UKG", label: "UKG" },
                            { value: "1st", label: "1st" },
                            { value: "2nd", label: "2nd" },
                            { value: "3rd", label: "3rd" },
                            { value: "4th", label: "4th" },
                            { value: "5th", label: "5th" },
                            { value: "6th", label: "6th" },
                            { value: "7th", label: "7th" },
                            { value: "8th", label: "8th" },
                            { value: "9th", label: "9th" },
                            { value: "10th", label: "10th" },
                            { value: "11th", label: "11th" },
                            { value: "12th", label: "12th" }
                        ],
                    },
                    { label: "Subject", name: "subject", type: "text", required: id ? false : true, placeholder: "Enter subject", },
                    { label: "Description", name: "description", type: "textarea", required: id ? false : true, placeholder: "Enter Brief detail of the assignment..", },
                    { label: "Submission Date", name: "submit_by", type: "date", required: id ? false : true, },
                    { label: "File", name: "file", type: "file", required: id ? false : true, },

                ]}
                onSubmit={handleFormSubmit}
                buttonText={!id ? "Add" : "Update"}
            />
        </>

    );
};

export default ManageAssignments;